/**
 * The world-date editor's model (1.Y deliverable 3), pure: a `world_date` field's placements and
 * the bodies of its writes. A field's value is its document's live placements carrying its
 * `fieldDefId`; a save is one `placements.create` (role `occurs`) or one `placements.update`.
 */
import type { CompiledCalendar, ParsedWhen } from '@codex/calendar';
import type { PlacementCreateV1, PlacementUpdateV1, PlacementV1 } from '@codex/contracts';
import { placementDate } from '../when/pill-model.js';
import { toPlacementDates } from '../when/when-model.js';
import { cap } from '../when/words.js';

const byOrder = (a: PlacementV1, b: PlacementV1) =>
  a.orderKey < b.orderKey ? -1 : a.orderKey > b.orderKey ? 1 : a.id < b.id ? -1 : 1;

/** The field's placements, in order. */
export function fieldPlacements(placements: readonly PlacementV1[], fieldDefId: string) {
  return placements.filter((p) => p.fieldDefId === fieldDefId).sort(byOrder);
}

/** "Late Mist 1042": the placement's date, its first letter capitalized. */
export function worldDateText(calendar: CompiledCalendar | null, placement: PlacementV1): string {
  return cap(placementDate(calendar, placement));
}

/** The dates of a read as a body: a moment from a point, null for anything else. */
function datesOf(parsed: ParsedWhen, rawInput: string, calendar: CompiledCalendar) {
  const dates = toPlacementDates(parsed, { lasts: false, rawInput, calendar });
  if (dates === null || dates.kind !== 'moment' || dates.start === null) return null;
  return { kind: dates.kind, start: dates.start, ongoing: dates.ongoing };
}

export function createBody(
  parsed: ParsedWhen,
  rawInput: string,
  calendar: CompiledCalendar,
  fieldDefId: string,
): PlacementCreateV1 | null {
  const dates = datesOf(parsed, rawInput, calendar);
  return dates === null ? null : { ...dates, fieldDefId, role: 'occurs' };
}

export function updateBody(
  parsed: ParsedWhen,
  rawInput: string,
  calendar: CompiledCalendar,
  placement: Pick<PlacementV1, 'rv'>,
): PlacementUpdateV1 | null {
  const dates = datesOf(parsed, rawInput, calendar);
  return dates === null ? null : { baseRv: placement.rv, ...dates, end: null };
}
